import PropTypes from 'prop-types';
import React from 'react';

import UniverseLogo from '../images/universe.png';
import LenndLogo from '../images/lennd.png';
import DigitickLogo from '../images/digitick.png';
import FnacLogo from '../images/fnac.png';
import Ticketmaster from '../images/ticketmaster.png';
import PlaypassLogo from '../images/playpass.png';
import SecutixLogo from '../images/secutix.png';
import RegiondoLogo from '../images/regiondo.png';
import PlaceholderImage from '../../../images/weez-placeholder.png';

import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { WeezHeader, Button, DropdownLine, CopyToClipboardLink, Chip, Status, DropdownItem, ButtonDropdown, Card, EmptyState, Icon, CardRow, ToastContext } from '@weezevent/nacre';
import Subscription from '../../../models/subscription';
import DeleteModal from './components/deleteModal';
import { usePermissions } from '../../../user/permissions';
import { fetcher } from '@weezevent/weezjs-core';
import Styles from './subscriptionsList.module.css';
import LoadingCard from '../../../components/loadingCard';
import useAppContext from '../../../context/useAppContext';
import i18n from 'i18next';
import { useHistory } from 'react-router-dom';

const SERVICE_LOGOS = {
    universe: UniverseLogo,
    lennd: LenndLogo,
    digitick: DigitickLogo,
    fnac: FnacLogo,
    ticketnet: Ticketmaster,
    playpass: PlaypassLogo,
    secutix: SecutixLogo,
    regiondo: RegiondoLogo
};

const distributorFields = {
    digitick: ['login', 'wsdl'],
    fnac: ['login'],
    universe: ['authCode'],
    lennd: ['accessToken'],
    ticketnet: ['login', 'account_type'],
    playpass: ['login', 'tenant', 'getin_url'],
    secutix: ['login', 'url'],
    regiondo: ['public_key', 'private_key']
};

function SubscriptionImage({ subscription }) {
    const image = SERVICE_LOGOS[subscription.type];

    if (!image) {
        return (
            <div
                className={Styles['subscription-image']}
                style={{
                    backgroundImage: `url(${PlaceholderImage})`
                }}
            />
        );
    }

    return (
        <div className={Styles['webservice-logo']}>
            {
                <div>
                    <img className={`webservice-logo ${subscription.type}`} src={image} alt={subscription.type} />
                </div>
            }
        </div>
    );
}
SubscriptionImage.propTypes = {
    subscription: PropTypes.object.isRequired
};

function LineItem({ title, children, subscription }) {
    return (
        <div className={`${Styles['line-item']} ${subscription?.type !== 'hook' ? Styles['line-item-distributor'] : ''}`}>
            <span>
                <span className={Styles['line-title']}>{title}</span>:
            </span>
            <span className={Styles['line-content']}>{children}</span>
        </div>
    );
}
LineItem.propTypes = {
    subscription: PropTypes.object.isRequired,
    title: PropTypes.node.isRequired
};

function SubscriptionStatus({ subscription }) {
    const { t } = useTranslation();

    const status = React.useMemo(() => {
        if (subscription.status === 'login_error') {
            return { color: 'red', text: t('ticket.webservice.list.unConnected') };
        }
        if (subscription.status === 'new') {
            return { color: 'orange', text: t('ticket.webservice.list.connection') };
        }
        return { color: 'green', text: t('ticket.webservice.list.connected') };
    }, [subscription]);

    return <Status className={Styles['subscription-status']} {...status} />;
}
SubscriptionStatus.propTypes = {
    subscription: PropTypes.object.isRequired
};

function FormatedDate({ date, icon, timezone, locale }) {
    if (!date) {
        return null;
    }
    const _date = moment
        .utc(date)
        .tz(timezone)
        .format('DD/MM/YYYY, HH:mm:ss');
    return <Chip primary icon={icon} text={_date} />;
}
FormatedDate.propTypes = {
    date: PropTypes.object,
    icon: PropTypes.string,
    timezone: PropTypes.string,
    locale: PropTypes.string
};

function SubscriptionCard({ organizationId, subscription, sync, onSelectedSubscription }) {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);
    const [{ organization }] = useAppContext();
    const locale = i18n.language;
    const history = useHistory();

    const nextUpdate = React.useMemo(() => {
        if (!subscription.last_check) {
            return null;
        }
        return moment(subscription.last_check).add(12, 'hours');
    }, [subscription]);

    const retryConnection = React.useCallback(() => {
        const subscriptionInstance = new Subscription(subscription);
        subscriptionInstance.status = 'new';
        subscriptionInstance
            .save({ organizationId })
            .then(() => {
                sync(true);
            })
            .catch(e => {
                toast.error(t('ticket.webservice.list.retryError'));
                console.error(e);
            });
    }, [subscription, organizationId, sync, toast]);

    const refreshSubscription = React.useCallback(() => {
        fetcher.post(`/ticket-imports/organizations/${organizationId}/subscriptions/${subscription.id}/refresh`).then(() => {
            toast.success(t('ticket.webservice.list.refreshSubscription'));
        });
    }, [subscription, organizationId]);

    const dynamicFields = React.useMemo(() => {
        for (const [key, values] of Object.entries(distributorFields)) {
            if (key === subscription.type) {
                return values.map(value => {
                    return (
                        <LineItem subscription={subscription} key={value} title={t(`ticket.webservice.list.${value}`)}>
                            <div>{subscription[value]}</div>
                        </LineItem>
                    );
                });
            }
        }
    }, [subscription, t]);

    const redirectionUrl = React.useMemo(() => {
        if (subscription.type === 'hook') {
            return `/ticket/O${organizationId}/webservices/${subscription.id}/attendees`;
        }
        if (subscription.status === 'login_error') {
            return `/ticket/O${organizationId}/webservices/edit/${subscription.id}`;
        }
        return `/ticket/O${organizationId}/webservices/${subscription.id}/subscription_links`;
    }, [subscription, organizationId]);

    const urlToCopy = React.useMemo(() => {
        const url = window.weezConfig.api.apiUrl;
        return `${url}/ticket-imports/organizations/${organizationId}/hooks/${subscription.hook_uuid}`;
    }, [organizationId, subscription]);

    const showRefreshButton = Boolean(
        subscription.type !== 'hook' &&
            subscription.status !== 'login_error' &&
            (permissions.isAdmin() || permissions.hasCapacity('WendyCapacity.WEBSERVICES_PULL_RESYNC', organizationId))
    );

    return (
        <CardRow as="a" className={Styles['webservice-card']} href={redirectionUrl}>
            <CardRow.LeftComponent>
                <div className={`${subscription.type === 'hook' ? 'hook-image-container' : ''}`}>
                    <SubscriptionImage subscription={subscription} />
                </div>
            </CardRow.LeftComponent>
            <CardRow.RightComponent>
                <div className={Styles['subscription-row-container']}>
                    <div className={Styles['subscription-infos']}>
                        {subscription.type === 'hook' ? (
                            <div className={Styles['infos']}>
                                <LineItem subscription={subscription} title={t('ticket.webservice.list.name')}>
                                    {subscription.name}
                                </LineItem>
                                <LineItem title={t('ticket.webservice.list.end_date')}>
                                    {subscription.end_date
                                        ? moment
                                              .utc(subscription.end_date)
                                              .tz(organization.timezone)
                                              .format('DD/MM/YYYY, HH:mm')
                                        : '-'}
                                </LineItem>
                                <LineItem subscription={subscription} title={t('ticket.webservice.list.url')}>
                                    <CopyToClipboardLink copyOnIconClick delay={1000} tooltipMini value={urlToCopy} tooltip={t('ticket.common.action.copy')}>
                                        {t('ticket.webservice.list.link-to-copy')}
                                    </CopyToClipboardLink>
                                </LineItem>
                            </div>
                        ) : (
                            <div className={Styles['infos']}>
                                <LineItem subscription={subscription} title={t('ticket.webservice.distributor')}>
                                    <div>{subscription.type}</div>
                                </LineItem>
                                {dynamicFields}
                            </div>
                        )}
                    </div>
                    <div className={Styles['subscription-right-container']}>
                        {subscription.type !== 'hook' && (
                            <>
                                <div className={Styles['subscription-dates']}>
                                    <FormatedDate date={subscription.last_check} icon="history" timezone={organization.timezone} locale={locale} />
                                    <FormatedDate date={nextUpdate} icon="refresh" timezone={organization.timezone} locale={locale} />
                                </div>
                                <div className={Styles['subscription-status']}>
                                    <SubscriptionStatus subscription={subscription} />
                                </div>
                            </>
                        )}
                        <ButtonDropdown
                            onClick={e => e.preventDefault()}
                            renderWhenClosed
                            trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                            items={[
                                <div key="items" className={Styles['dropdown-button-items']} onClick={e => e.preventDefault()}>
                                    {/* Retry connection */}
                                    {subscription.status === 'login_error' && subscription.type !== 'hook' && (
                                        <DropdownItem key="retry-connection" item={t('ticket.webservice.list.retryConnection')} onClick={retryConnection} />
                                    )}

                                    {/* Refresh subscription */}
                                    {showRefreshButton ? (
                                        <DropdownItem key="refresh-subscription" item={t('ticket.webservice.list.refresh')} onClick={refreshSubscription} />
                                    ) : null}

                                    {/* Edit subscription */}
                                    {permissions.isAdmin() && (['login_error', 'login_ok', 'login_ignore'].includes(subscription.status) || subscription.type === 'hook') && (
                                        <DropdownItem
                                            key="edit-subscription"
                                            item={t('ticket.webservice.list.edit')}
                                            onClick={() => history.push(`/ticket/O${organizationId}/webservices/edit/${subscription.id}`)}
                                        />
                                    )}

                                    {/* Remove subscription */}
                                    {subscription.type !== 'hook' && (
                                        <>
                                            <DropdownLine />
                                            <DropdownItem key="remove" deleteItem item={t('ticket.webservice.list.remove')} onClick={() => onSelectedSubscription(subscription)} />
                                        </>
                                    )}
                                </div>
                            ]}
                        />
                    </div>
                </div>
            </CardRow.RightComponent>
        </CardRow>
    );
}

SubscriptionCard.propTypes = {
    subscription: PropTypes.object.isRequired,
    organizationId: PropTypes.string.isRequired,
    reload: PropTypes.func.isRequired,
    onSelectedSubscription: PropTypes.func.isRequired
};

export default function SubscriptionsList({ organizationId }) {
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const permissions = usePermissions();
    const [selectedSubscription, setSelectedSubscription] = React.useState(null);
    const [subscriptions_obj, setSubscription] = React.useState([]);

    const { t } = useTranslation();

    const [, loadingSubscriptions, sync] = Subscription.useApiModel(
        {
            organizationId: organizationId,
            cache: false,
            onError: () => {
                toast.error(t('ticket.webservice.list.retrievalError'));
            },
            onSuccess: subscription => {
                let subs = subscription.map(sub => sub.object);
                if (permissions.hasCapacity('WendyCapacity.WEBSERVICES_PUSH', organizationId)) {
                    setSubscription(subs);
                } else {
                    setSubscription(subs.filter(sub => sub.type !== 'hook'));
                }
            }
        },
        [organizationId, permissions, setSubscription]
    );

    const onCloseModal = React.useCallback(() => {
        setSelectedSubscription(null);
    }, []);

    return (
        <>
            <WeezHeader
                title={t('ticket.webservice.list.titleHeader')}
                rightComponent={
                    <Button
                        primary
                        icon={<Icon name="plus" margin={false} />}
                        label={t('ticket.webservice.addWebServices')}
                        onClick={() => history.push(`/ticket/O${organizationId}/webservices/new`)}
                    />
                }
            >
                {t('ticket.webservice.list.sub')}
            </WeezHeader>
            <LoadingCard loading={loadingSubscriptions} height="60vh">
                {subscriptions_obj?.length > 0 ? (
                    <>
                        <DeleteModal subscription={selectedSubscription} show={Boolean(selectedSubscription)} onClose={onCloseModal} sync={sync} organizationId={organizationId} />
                        {subscriptions_obj.map((subscription, key) => (
                            <>
                                <SubscriptionCard
                                    key={key}
                                    subscription={subscription}
                                    organizationId={organizationId}
                                    sync={sync}
                                    history={history}
                                    onSelectedSubscription={setSelectedSubscription}
                                />
                            </>
                        ))}
                    </>
                ) : (
                    <Card>
                        <EmptyState
                            icon={<Icon name="list" />}
                            title={t('ticket.webservice.list.emptyTitle')}
                            subtitle={t('ticket.webservice.list.emptySub')}
                            labelButton={t('ticket.webservice.addWebServices')}
                            onClick={() => history.push(`/ticket/O${organizationId}/webservices/new`)}
                        />
                    </Card>
                )}
            </LoadingCard>
        </>
    );
}
SubscriptionsList.propTypes = {
    organizationId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired
};
